import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock, Loader2 } from 'lucide-react';

const FALLBACK_BG = "https://images.unsplash.com/photo-1558542403-bb396707e7da?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920";
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Branded fallback logo (mosque silhouette + crescent) shown when the school
// hasn't uploaded a logo yet. Pure SVG so it always renders and never 404s.
const FallbackLogoSVG = ({ className = '', ...rest }) => (
    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" className={className} {...rest} aria-label="Nurulhuda Quranic School">
        <defs>
            <linearGradient id="nhqGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0F766E" />
                <stop offset="100%" stopColor="#064E3B" />
            </linearGradient>
        </defs>
        <circle cx="64" cy="64" r="60" fill="url(#nhqGrad)" />
        {/* Crescent */}
        <path d="M64 22 a26 26 0 1 0 22 40 a20 20 0 1 1 -22 -40 z" fill="#FDE68A" />
        {/* Star */}
        <path d="M92 44 l3 8 l8 1 l-6 6 l2 8 l-7 -4 l-7 4 l2 -8 l-6 -6 l8 -1 z" fill="#FDE68A" />
        {/* Mosque base */}
        <rect x="30" y="78" width="68" height="26" rx="3" fill="#F5F5F4" />
        {/* Main dome */}
        <path d="M42 78 q22 -24 44 0 z" fill="#F5F5F4" />
        {/* Doorway */}
        <path d="M60 88 q4 -6 8 0 v16 h-8 z" fill="#064E3B" />
        {/* Minarets */}
        <rect x="26" y="70" width="6" height="34" fill="#F5F5F4" />
        <rect x="96" y="70" width="6" height="34" fill="#F5F5F4" />
        <circle cx="29" cy="68" r="4" fill="#F5F5F4" />
        <circle cx="99" cy="68" r="4" fill="#F5F5F4" />
    </svg>
);

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    // Point directly at the public logo endpoint — no auth needed. The endpoint
    // 302-redirects to settings.logo_url when set (Super Admin uploads via /admin/settings).
    // If no logo is configured yet, the endpoint returns 404 and we fall back to
    // the branded inline SVG via the onError handler.
    const [logoError, setLogoError] = useState(false);
    const logoUrl = `${API_URL}/api/public/logo`;
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(email, password);
        
        if (result.success) {
            // Redirect based on role
            const role = result.user.role;
            if (role === 'super_admin') {
                navigate('/admin');
            } else if (role === 'manager') {
                navigate('/manager');
            } else if (role === 'teacher') {
                navigate('/teacher');
            } else {
                navigate('/parent');
            }
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    // Background: use the school logo as a subtle watermark when it's configured,
    // otherwise fall back to a hero image so the page never looks empty.
    const bgImage = logoError ? FALLBACK_BG : logoUrl;
    const bgHasLogo = !logoError;
    return (
        <div className="min-h-screen flex relative">
            {/* Full-page school logo background */}
            <div className="absolute inset-0 z-0">
                <div
                    className="absolute inset-0 bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(${bgImage})`,
                        backgroundSize: bgHasLogo ? 'contain' : 'cover',
                        opacity: bgHasLogo ? 0.18 : 1,
                    }}
                />
                <div className="absolute inset-0 bg-[#FAF9F6]/85" />
            </div>

            {/* Left side - Hero section */}
            <div className="hidden lg:flex lg:w-1/2 relative z-10 flex-col justify-center items-center p-12">
                <div className="w-44 h-44 mb-6 rounded-2xl overflow-hidden drop-shadow-lg bg-white/70 p-3 flex items-center justify-center" data-testid="login-hero-logo">
                    {!logoError ? (
                        <img src={logoUrl} alt="Nurulhuda Quranic School logo" className="w-full h-full object-contain" onError={() => setLogoError(true)} />
                    ) : (
                        <FallbackLogoSVG className="w-full h-full" />
                    )}
                </div>
                <h1 className="text-4xl font-bold text-center mb-4 font-['Outfit'] text-[#064E3B]">
                    Nurulhuda Quranic School
                </h1>
                <p className="text-lg text-center text-[#4B5563] max-w-md">
                    Empowering students through Quranic education and Islamic knowledge
                </p>
            </div>

            {/* Right side - Login Form */}
            <div className="flex-1 flex flex-col justify-center items-center p-8 relative z-10">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex flex-col items-center mb-8">
                        <div className="w-24 h-24 mb-4 rounded-xl overflow-hidden drop-shadow bg-white/70 p-2 flex items-center justify-center" data-testid="login-mobile-logo">
                            {!logoError ? (
                                <img src={logoUrl} alt="Nurulhuda Quranic School logo" className="w-full h-full object-contain" onError={() => setLogoError(true)} />
                            ) : (
                                <FallbackLogoSVG className="w-full h-full" />
                            )}
                        </div>
                        <h1 className="text-2xl font-bold text-[#064E3B] font-['Outfit']">
                            Nurulhuda Quranic School
                        </h1>
                    </div>

                    <div className="bg-white rounded-xl border border-black/5 shadow-sm p-8">
                        <h2 className="text-2xl font-semibold text-[#1F2937] mb-2 font-['Outfit']">
                            Welcome Back
                        </h2>
                        <p className="text-[#4B5563] mb-6">
                            Sign in to access your dashboard
                        </p>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-[#1F2937]">Email Address</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#4B5563]" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10 input-field"
                                        required
                                        data-testid="login-email-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-[#1F2937]">Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#4B5563]" />
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 pr-10 input-field"
                                        required
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#1F2937]"
                                    >
                                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center justify-end">
                                <Link 
                                    to="/forgot-password" 
                                    className="text-sm text-[#064E3B] hover:text-[#047857] font-medium"
                                    data-testid="forgot-password-link"
                                >
                                    Forgot Password?
                                </Link>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-[#064E3B] text-white hover:bg-[#047857] h-11"
                                data-testid="login-submit-button"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Signing in...
                                    </>
                                ) : (
                                    'Sign In'
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <p className="text-[#4B5563]">
                                New parent?{' '}
                                <Link 
                                    to="/register" 
                                    className="text-[#064E3B] hover:text-[#047857] font-medium"
                                    data-testid="register-link"
                                >
                                    Register Here
                                </Link>
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-8 text-center text-sm text-[#4B5563]">
                        <p>© {new Date().getFullYear()} Nurulhuda Quranic School. All rights reserved.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
